import { BiRadioCircleMarked } from 'react-icons/bi';

import type { DrilledHole } from '~api/services';
import { PropertyGroup, PropertyItem } from '~components';
import { ScrollArea } from '~components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~components/ui/sheet';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type DrilledHoleDetailsProps = {
  /** The drilled hole data to display */
  hole: DrilledHole | null;
  /** The container element to render the drawer into */
  container?: HTMLElement;
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when the drawer is closed */
  onClose: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const HOLE_PARAMETERS: { label: string; getValue: (hole: DrilledHole) => string | number }[] = [
  { label: 'hardness', getValue: (h) => h.hardness ?? '-' },
  { label: 'hardness_planned', getValue: (h) => h.hardness_planned ?? '-' },
  { label: 'hazardous', getValue: (h) => h.hazardous ?? '-' },
  { label: 'drill domain', getValue: (h) => h.drill_domain_id ?? '-' },
  { label: 'blast domain', getValue: (h) => h.blast_domain_id ?? '-' },
  { label: 'hole type', getValue: (h) => h.holetype ?? '-' },
  { label: 'source', getValue: (h) => h.source_name ?? '-' },
  { label: 'destination', getValue: (h) => h.destination ?? '-' },
  { label: 'cavity', getValue: (h) => h.cavity ?? '-' },
  { label: 'strand type', getValue: (h) => h.strandtype ?? '-' },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sheet component to display details of a drilled hole, including parameters such as hardness, drill domain, and blast domain.
 *
 * @example
 * ```tsx
 *   <DrilledHoleDetailsDrawer
 *     hole={selectedHoleData}
 *     container={drawerContainer}
 *     open={isDrawerOpen}
 *     onClose={() => setIsDrawerOpen(false)}
 *   />
 * ```
 */
const DrilledHoleDetailsDrawer = ({ hole, container, open, onClose }: DrilledHoleDetailsProps) => {
  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <SheetContent
        portalContainer={container ?? document.body}
        side="inline-end"
        strategy="absolute"
        showOverlay={false}
        className="grow border-t-0"
        data-open={open}
        aria-describedby={undefined}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader>
          {hole ? (
            <SheetTitle className="flex items-center gap-2">
              <BiRadioCircleMarked className="rounded-full bg-primary/10 p-1 text-primary" size={32} />
              Hole: <span>{hole.hole_name}</span>
            </SheetTitle>
          ) : (
            <SheetTitle>Hole Details</SheetTitle>
          )}
        </SheetHeader>
        <div className="flex min-h-0 flex-1">
          <ScrollArea className="h-full w-full" type="auto">
            {hole && (
              <div className="space-y-6 p-6">
                <PropertyGroup>
                  {HOLE_PARAMETERS.map(({ label, getValue }) => (
                    <PropertyItem key={label} label={label} value={getValue(hole)} contained />
                  ))}
                </PropertyGroup>
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { DrilledHoleDetailsDrawer };
